import React, { createContext, useContext, useState, useEffect } from 'react';
import { merchantApi } from '../api/config';

interface AuthContextType {
  isAuthenticated: boolean;
  merchant: any;
  mustChangePassword: boolean;
  setMustChangePassword: (value: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [merchant, setMerchant] = useState<any>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ⭐ ป้องกันวนลูป - ไม่ต้อง checkAuth ถ้าอยู่หน้า login หรือ admin
    const currentPath = window.location.pathname;
    if (currentPath === '/login' || currentPath.startsWith('/admin') || currentPath.startsWith('/reseller') || currentPath.startsWith('/agent')) {
      // Admin/Reseller/Agent ใช้ระบบ auth แยก
      // Merchant auth ไม่จำเป็นสำหรับ pages เหล่านี้
      setLoading(false);
      setIsAuthenticated(false);
      return;
    }
    
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // ✅ ไม่ต้องเช็ค localStorage - ใช้ cookies แทน
    try {
      const response = await merchantApi.get('/auth/merchant/me');
      setMerchant(response.data.data.user);
      setMustChangePassword(!!response.data.data.user?.must_change_password);
      setIsAuthenticated(true);
    } catch (error) {
      // ถ้า error = ไม่มี cookie หรือ cookie หมดอายุ
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await merchantApi.post('/auth/merchant/login', {
        email,
        password
      });

      const data = response.data.data;

      // ตรวจสอบว่าต้องการ 2FA หรือไม่
      if (data.requires_2fa) {
        // Throw error พิเศษที่มี merchant_id และ session_conflict
        const error: any = new Error(response.data.message || 'กรุณากรอกรหัส OTP');
        error.requires_2fa = true;
        error.merchant_id = data.merchant_id;
        error.merchant_email = data.email;
        error.session_conflict = data.session_conflict || false;
        error.existing_session = data.existing_session || null;
        throw error;
      }

      // ✅ ไม่ต้องเก็บ token - cookies ถูกตั้งโดย backend แล้ว
      const { merchant } = data;
      
      setMerchant(merchant);
      setIsAuthenticated(true);

      // ✅ เริ่ม session check ทุก 30 วินาที
      startSessionCheck();
    } catch (error: any) {
      throw error;
    }
  };

  // 🔒 Reference สำหรับ interval (ป้องกัน memory leak)
  const sessionCheckRef = React.useRef<NodeJS.Timeout | null>(null);

  // เช็ค session ทุก 30 วินาที (สำหรับ Merchant เท่านั้น)
  const startSessionCheck = () => {
    // 🔒 Clear interval เดิมก่อน (ป้องกันสร้างซ้ำ)
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
    }

    const checkSession = async () => {
      // ⭐ ไม่ต้องเช็คถ้าอยู่หน้า login, admin หรือ reseller
      const currentPath = window.location.pathname;
      if (currentPath === '/login' || currentPath.startsWith('/admin') || currentPath.startsWith('/reseller')) {
        return;
      }
      
      try {
        await merchantApi.get('/auth/merchant/me');
      } catch (error: any) {
        if (error.response?.data?.session_expired) {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ Session expired - logging out');
          }
          setIsAuthenticated(false);
          // ⭐ ไม่ต้อง alert และ redirect ที่นี่ 
          // เพราะ interceptor ทำให้แล้ว (ป้องกันซ้ำซ้อน)
        }
      }
    };

    // เช็คทันทีแล้วเช็คทุก 30 วินาที
    checkSession();
    sessionCheckRef.current = setInterval(checkSession, 30000);
  };

  // 🔒 Cleanup interval เมื่อ component unmount
  useEffect(() => {
    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, []);

  const logout = async () => {
    try {
      // ✅ เรียก backend ให้ลบ cookie
      await merchantApi.post('/auth/logout');
    } catch (error) {
      // ไม่สำคัญถ้า error - logout ต่อไป
    }
    
    setMerchant(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, merchant, mustChangePassword, setMustChangePassword, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

